import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Vendor } from "../types";
import { useToast } from "../toast";
import VendorDialog from "../dialogs/VendorDialog";
import ConfirmDialog from "../dialogs/ConfirmDialog";

export default function Vendors() {
  const api = useAuthApi();
  const [items, setItems] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [deleting, setDeleting] = useState<Vendor | null>(null);
  const [search, setSearch] = useState("");
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<Vendor[]>(`${MGMT}/vendors`);
      setItems(data ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api(`${MGMT}/vendors/${deleting.id}`, { method: "DELETE" });
      toast.show("Vendor deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (vendor) =>
        vendor.name.toLowerCase().includes(q)
        || vendor.label?.key.toLowerCase().includes(q)
        || vendor.label?.value.toLowerCase().includes(q)
    );
  }, [items, search]);

  const hasFilters = search.trim().length > 0;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          Add Vendor
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Filter vendors…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {hasFilters && <Button size="small" onClick={() => setSearch("")}>Clear filters</Button>}
      </Stack>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Label</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      {hasFilters ? "No vendors match the current filters." : "No vendors yet."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((vendor) => (
              <TableRow key={vendor.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{vendor.name}</TableCell>
                <TableCell>{vendor.label ? `${vendor.label.key}: ${vendor.label.value}` : "—"}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(vendor)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleting(vendor)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <VendorDialog
        open={creating || editing !== null}
        vendor={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          load();
        }}
      />
      <ConfirmDialog
        open={deleting !== null}
        title="Delete vendor?"
        message={`Delete vendor "${deleting?.name}"? Charger models using this vendor must be updated first.`}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
